import React from 'react';
import {
    Skeleton,
    styled,
    Typography,
    TypographyProps,
} from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';

import { ApiStatus, Grade, ValueReferences } from '../../utils/enums';
import { getGradeColor } from '../../utils/styleUtils';

interface StyleProps extends TypographyProps {
    grade: Grade | null;
}

const classesPrefix = 'gradeLabel';

const classes = {
    grade: `${classesPrefix}-grade`,
};

const StyledTypography = styled(
    Typography,
    {
        // This prevents passing the StyleProps down to the component
        shouldForwardProp: (prop) => { return prop !== 'grade' && prop !== 'apiStatus'; },
    }
)<StyleProps>(({ grade, theme }) => {
    return {
        [`&.${classes.grade}`]: {
            color: getGradeColor(grade, theme),
            display: 'inline-block',
        },
    };
});

interface GradeLabelProps {
    apiStatus: ApiStatus;
    grade: Grade | null;
    variant: Variant;
}

const GradeLabel = React.forwardRef<HTMLSpanElement, GradeLabelProps>((props: GradeLabelProps, ref): React.JSX.Element => {
    const { apiStatus, grade, variant } = props;

    const renderGrade = (): React.ReactNode => {
        if (apiStatus === ApiStatus.Idle || apiStatus === ApiStatus.Loading) {
            return <Skeleton width={60} />;
        }

        if (apiStatus === ApiStatus.Failure || grade === null) {
            return ValueReferences.NA;
        }

        return grade;
    };

    return (
        <StyledTypography
            ref={ref}
            {...props}
            className={classes.grade}
            grade={grade}
            variant={variant}
        >
            {renderGrade()}
        </StyledTypography>
    );
});
GradeLabel.displayName = 'GradeLabel';

export default GradeLabel;
